import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { LiveMatch } from "@/components/LiveMatch";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { UpcomingMatchCard } from "@/components/UpcomingMatchCard";
import fanHero from "@/assets/fan-hero-optimized.jpg";
import formationField from "@/assets/formation-4231-optimized.jpg";
import playerCelebration from "@/assets/player-celebration-optimized.jpg";

interface HeroImage {
  id: string;
  image_type: string;
  image_url: string;
  updated_at?: string;
}

// Helper function to get initial hero images from localStorage cache
const getInitialHeroImages = () => {
  try {
    const storedData = JSON.parse(localStorage.getItem('heroImageTimestamps') || '{}');
    const imageMap: any = {
      hero_main: fanHero,
      formation_field: formationField,
      player_celebration: playerCelebration,
    };
    
    // Use cached URLs with timestamps if available
    Object.keys(storedData).forEach((key) => {
      if (storedData[key]?.url && storedData[key]?.timestamp) {
        imageMap[key] = `${storedData[key].url}?t=${storedData[key].timestamp}`;
      }
    });
    
    return imageMap;
  } catch {
    // Fallback to default images if cache parsing fails
    return {
      hero_main: fanHero,
      formation_field: formationField,
      player_celebration: playerCelebration,
    };
  }
};

const Index = () => {
  const [homeScore, setHomeScore] = useState("");
  const [awayScore, setAwayScore] = useState("");
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLiveMatch, setHasLiveMatch] = useState(false);
  const [heroImages, setHeroImages] = useState(getInitialHeroImages());

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const now = new Date();
        
        // Combine all API calls into one parallel request
        const [matchesResult, heroImagesResult, liveMatchResult] = await Promise.all([
          supabase
            .from('live_matches')
            .select('*')
            .gte('match_date', now.toISOString())
            .order('match_date', { ascending: true })
            .limit(1),
          supabase
            .from('hero_images')
            .select('id, image_type, image_url, updated_at'),
          supabase
            .from('live_matches')
            .select('id')
            .eq('status', 'live')
            .maybeSingle()
        ]);

        // Process matches
        if (matchesResult.data && matchesResult.data.length > 0) {
          const formattedMatches = matchesResult.data.map((match: any) => {
            const matchDate = new Date(match.match_date);
            return {
              date: matchDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
              homeTeam: match.home_team,
              awayTeam: match.away_team,
              time: matchDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
              competition: match.competition,
              matchDate: match.match_date,
            };
          });
          setMatches(formattedMatches);
        }

        // Process hero images - signature-based caching with updated_at detection
        if (heroImagesResult.data) {
          const imageMap: any = {
            hero_main: fanHero,
            formation_field: formationField,
            player_celebration: playerCelebration,
          };

          const storedData = JSON.parse(localStorage.getItem('heroImageTimestamps') || '{}');
          const currentData: any = {};

          heroImagesResult.data.forEach((img: HeroImage) => {
            if (img.image_url !== 'default') {
              const stored = storedData[img.image_type];
              
              // Create signature from URL + updated_at to detect any changes
              const signature = `${img.image_url}|${img.updated_at ?? ''}`;
              
              // Signature has changed (new URL or updated_at) or doesn't exist -> new timestamp
              if (!stored || stored.signature !== signature) {
                currentData[img.image_type] = {
                  url: img.image_url,
                  signature: signature,
                  timestamp: Date.now()
                };
              } else {
                // Signature is the same -> keep old timestamp (use browser cache)
                currentData[img.image_type] = stored;
              }
              
              const finalUrl = `${img.image_url}?t=${currentData[img.image_type].timestamp}`;
              imageMap[img.image_type] = finalUrl;
              
              // Optional: Preload image for smooth transition
              const preloadImg = new Image();
              preloadImg.src = finalUrl;
            }
          });

          localStorage.setItem('heroImageTimestamps', JSON.stringify(currentData));
          setHeroImages(imageMap);
        }

        // Process live match status
        setHasLiveMatch(!!(liveMatchResult.data && !liveMatchResult.error));

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  useRealtimeSubscription({
    table: 'live_matches',
    event: '*',
    callback: async () => {
      const { data } = await supabase
        .from('live_matches')
        .select('id')
        .eq('status', 'live')
        .maybeSingle();
      setHasLiveMatch(!!data);
    },
  });

  const handlePrediction = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Prognose abgegeben!");
    setHomeScore("");
    setAwayScore("");
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--dark-red))]">
      <Navigation />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-[hsl(var(--dark-red))] overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 items-center min-h-[500px]">
              <div className="py-8 md:py-12 lg:py-0 text-white animate-fade-in">
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-4 md:mb-6 uppercase tracking-tight text-[hsl(var(--golden-yellow))]">
                  YUVANIZA<br />HOŞ GELDİNİZ
                </h1>
                <p className="text-lg md:text-xl mb-6 md:mb-8 text-white/90 max-w-md">
                  Futbol taraftar topluluğuna katıl. Maç tahminleri yap, kadrolara oy ver ve görüşlerini paylaş.
                </p>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-gradient-cta hover:opacity-90 transition-opacity text-dark-red font-bold text-base md:text-lg px-6 py-4 md:px-8 md:py-6 rounded-xl uppercase tracking-wide shadow-lg"
                >
                  <Link to="/prognosen">Tahmin yap</Link>
                </Button>
              </div>
              <div className="hidden lg:block">
                <picture>
                  <source 
                    srcSet={(heroImages.hero_main || fanHero).replace(/\.(jpg|jpeg|png)$/i, '.webp')} 
                    type="image/webp" 
                  />
                  <img 
                    key={heroImages.hero_main}
                    src={heroImages.hero_main} 
                    alt="Celebrating football fan" 
                    className="w-full h-[500px] object-cover object-center rounded-l-3xl shadow-2xl"
                    loading="eager"
                    fetchPriority="high"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* Upcoming Games Section */}
        <section className="bg-golden-yellow py-8 md:py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-5xl font-black uppercase mb-8 md:mb-12 text-center text-dark-red break-words">
              {hasLiveMatch ? 'Live Match' : 'Gelecek karşılaşma'}
            </h2>
            
            {hasLiveMatch ? (
              <div className="max-w-3xl mx-auto">
                <LiveMatch />
              </div>
            ) : loading ? (
              <Card className="p-8 bg-dark-red border-4 border-golden-yellow rounded-3xl shadow-2xl">
                <div className="text-center text-golden-yellow font-bold text-xl">Maçlar yükleniyor...</div>
              </Card>
            ) : matches.length > 0 ? (
              <div className="space-y-4">
                {matches.map((match, index) => (
                  <UpcomingMatchCard key={index} match={match} />
                ))}
              </div>
            ) : (
              <Card className="p-8 bg-dark-red border-4 border-golden-yellow rounded-3xl shadow-2xl">
                <div className="text-center text-golden-yellow font-bold text-xl">Mevcut maç yok</div>
              </Card>
            )}
          </div>
        </section>

        {/* Choose Ideal Starting Lineup */}
        <section className="bg-dark-red py-8 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase mb-6 md:mb-8 text-golden-yellow">
                  En ideal<br />ilk 11'i seç
                </h2>
                <Button 
                  asChild
                  size="lg"
                  className="bg-golden-yellow text-dark-red hover:bg-golden-yellow/90 font-black text-lg md:text-xl px-6 py-4 md:px-12 md:py-6 rounded-2xl uppercase tracking-wide shadow-lg border-4 border-golden-yellow"
                >
                  <Link to="/lineup-builder">Şimdi seç</Link>
                </Button>
              </div>

              <div className="relative">
                <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl border-4 border-golden-yellow">
                  <picture>
                    <source 
                      srcSet={(heroImages.formation_field || formationField).replace(/\.(jpg|jpeg|png)$/i, '.webp')} 
                      type="image/webp" 
                    />
                    <img 
                      key={heroImages.formation_field}
                      src={heroImages.formation_field} 
                      alt="Football formation field" 
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </picture>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Player of the Match */}
        <section className="bg-golden-yellow py-8 md:py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-5xl font-black uppercase mb-8 md:mb-12 text-center text-dark-red">
              Maçın adamı
            </h2>

            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8 items-center">
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                  <picture>
                    <source 
                      srcSet={(heroImages.player_celebration || playerCelebration).replace(/\.(jpg|jpeg|png)$/i, '.webp')} 
                      type="image/webp" 
                    />
                    <img 
                      key={heroImages.player_celebration}
                      src={heroImages.player_celebration} 
                      alt="Player celebrating" 
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </picture>
                </div>
              </div>

              <div className="text-center">
                <div className="relative inline-block mb-6 md:mb-8">
                  <svg className="w-48 h-48 md:w-64 md:h-64" viewBox="0 0 200 200">
                    {/* Background circle */}
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="hsl(40, 50%, 92%)"
                      stroke="hsl(0, 55%, 35%)"
                      strokeWidth="8"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      stroke="hsl(0, 55%, 35%)"
                      strokeWidth="12"
                      strokeDasharray="502.65"
                      strokeDashoffset="150.8"
                      strokeLinecap="round"
                      transform="rotate(-90 100 100)"
                    />
                    {/* Percentage text */}
                    <text
                      x="100"
                      y="110"
                      textAnchor="middle"
                      fontSize="48"
                      fontWeight="bold"
                      fill="hsl(0, 55%, 35%)"
                    >
                      72%
                    </text>
                  </svg>
                </div>
                <p className="text-lg md:text-xl text-dark-red mb-4 md:mb-6">
                  Taraftarlar bu oyuncuyu seçti
                </p>
                <Button 
                  asChild
                  size="lg"
                  className="bg-dark-red hover:opacity-90 transition-opacity text-golden-yellow font-bold text-lg md:text-xl px-6 py-4 md:px-12 md:py-6 rounded-2xl uppercase tracking-wide shadow-lg border-4 border-dark-red"
                >
                  <Link to="/abstimmungen">Oyunu ver</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Index;
